import { computed, inject, Injectable } from '@angular/core';
import { signalStore, withState, withMethods, withComputed } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, map, of, pipe, switchMap, tap } from 'rxjs';
import { ELoadingEntity } from 'src/models/generic-store.model';
import { withDevtools, withStorageSync, updateState } from '@angular-architects/ngrx-toolkit';
import { ELoginDeviceType, IAuthState } from 'src/models/auth.model';
import { AuthRestApiService } from './auth-rest-api.service';
import { withError, withLoading, withoutError, withoutLoading, withRequestStatus } from '../store-features/with-request-status.store-feature';
import { baseState } from 'src/models/common.model';
import dayjs from 'dayjs';

enum EAuthActions {
  TOKEN_DATA_REQUEST = '[Auth] Token Data Request',
  TOKEN_DATA_RESPONSE = '[Auth] Token Data Response',
  TOKEN_DATA_FAILURE = '[Auth] Token Data Failure',

  REFRESH_TOKEN_REQUEST = '[Auth] Refresh Token Request',
  REFRESH_TOKEN_RESPONSE = '[Auth] Refresh Token Response',
  REFRESH_TOKEN_FAILURE = '[Auth] Refresh Token Failure',

  CURRENT_USER_REQUEST = '[Auth] Current User Request',
  CURRENT_USER_RESPONSE = '[Auth] Current User Response',
  CURRENT_USER_FAILURE = '[Auth] Current User Failure',

  LOGOUT_REQUEST = '[Auth] Logout Request',
  LOGOUT_RESPONSE = '[Auth] Logout Response',
  LOGOUT_FAILURE = '[Auth] Logout Failure',

  RESET_USER = '[Auth] Reset user',
  RESET_STATE = '[Auth] Reset state',
  SET_REFRESH_TOKEN_EXPIRES = '[Auth] Set Refresh Token Expires',
  CLEAR_REFRESH_TOKEN_EXPIRES = '[Auth] Clear Refresh Token Expires',

  CLEAR_ERROR = '[Auth] Clear Error',
}

const initialAuthState: IAuthState = {
  tokenData: null,
  logoutIdToken: null,
  refreshTokenExpires: null,
};

const STORE_NAME = 'auth';

@Injectable({ providedIn: 'root' })
export class AuthStore extends signalStore(
  withState(initialAuthState),
  withStorageSync(STORE_NAME + 'State'),
  withDevtools(STORE_NAME),
  withRequestStatus(),
  withComputed(store => ({
    accessToken: computed<string>(() => store.tokenData()?.access_token ?? ''),
    showLoginLoader: computed<boolean>(() => store.isLoading(ELoadingEntity.AUTH_TOKEN_DATA)),
  })),
  withMethods((store, authRestApiService = inject(AuthRestApiService)) => {

    const getAuthTokenData = rxMethod<{ type: ELoginDeviceType, code: string, redirectUri: string }>(
      pipe(
        tap(() =>
          updateState(store, EAuthActions.TOKEN_DATA_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.AUTH_TOKEN_DATA, loadingProps),
          }))
        ),
        switchMap(({ type, code, redirectUri }) => {
          const tokenRequest = type === ELoginDeviceType.MOBILE
            ? authRestApiService.backendLoginMobile(code, redirectUri)
            : authRestApiService.backendLoginWeb(code, redirectUri);
          return tokenRequest.pipe(
            map((response) => {
              const expiresInMs = response.expires_in * 1000;
              const newExpireDate = dayjs().add(expiresInMs, 'milliseconds');

              return updateState(store, EAuthActions.TOKEN_DATA_RESPONSE, ({ loadingProps, errorProps }) => ({
                tokenData: response,
                refreshTokenExpires: newExpireDate.toISOString(),
                logoutIdToken: null,
                ...withoutLoading(ELoadingEntity.AUTH_TOKEN_DATA, loadingProps),
                ...withoutError(ELoadingEntity.AUTH_TOKEN_DATA, errorProps),
              }))
            }),
            catchError((error) =>
              of(
                updateState(store, EAuthActions.TOKEN_DATA_FAILURE, ({ loadingProps, errorProps }) => ({
                  tokenData: null,
                  logoutIdToken: null,
                  refreshTokenExpires: null,
                  ...withoutLoading(ELoadingEntity.AUTH_TOKEN_DATA, loadingProps),
                  ...withError(ELoadingEntity.AUTH_TOKEN_DATA, { error, errorParams: { code, redirectUri } }, errorProps),
                }))
              )
            )
          )
        })
      )
    );

    const logoutAndGetId = rxMethod<{ accessToken: string }>(
      pipe(
        tap(() =>
          updateState(store, EAuthActions.LOGOUT_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.AUTH_LOGOUT_ID_TOKEN, loadingProps),
          }))
        ),
        switchMap(({ accessToken }) =>
          authRestApiService.logoutBackend(accessToken).pipe(
            map((response) =>
              updateState(store, EAuthActions.LOGOUT_RESPONSE, ({ loadingProps, errorProps }) => ({
                tokenData: null,
                logoutIdToken: response.idToken,
                ...withoutLoading(ELoadingEntity.AUTH_LOGOUT_ID_TOKEN, loadingProps),
                ...withoutError(ELoadingEntity.AUTH_LOGOUT_ID_TOKEN, errorProps),
              }))
            ),
            catchError((error) =>
              of(
                updateState(store, EAuthActions.LOGOUT_FAILURE, ({ loadingProps, errorProps }) => ({
                  tokenData: null,
                  logoutIdToken: null,
                  ...withoutLoading(ELoadingEntity.AUTH_LOGOUT_ID_TOKEN, loadingProps),
                  ...withError(ELoadingEntity.AUTH_LOGOUT_ID_TOKEN, { error, errorParams: { accessToken } }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    const resetState = () => {
      updateState(store, EAuthActions.RESET_STATE, { ...initialAuthState, ...baseState });
    }

    const setRefreshTokenExpires = (expires: string) => {
      updateState(store, EAuthActions.SET_REFRESH_TOKEN_EXPIRES, { refreshTokenExpires: expires });
    }

    const clearRefreshTokenExpires = () => {
      updateState(store, EAuthActions.CLEAR_REFRESH_TOKEN_EXPIRES, { refreshTokenExpires: null });
    }

    const refreshToken = rxMethod<{ type: ELoginDeviceType, refreshToken: string }>(
      pipe(
        tap(() =>
          updateState(store, EAuthActions.REFRESH_TOKEN_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.AUTH_TOKEN_DATA, loadingProps),
          }))
        ),
        switchMap(({ type, refreshToken }) => {
          const refreshRequest = type === ELoginDeviceType.MOBILE
            ? authRestApiService.refreshTokenMobile(refreshToken)
            : authRestApiService.refreshTokenWeb(refreshToken);
          return refreshRequest.pipe(
            map((response) => {
              const expiresInMs = response.expires_in * 1000;
              const newExpireDate = dayjs().add(expiresInMs, 'milliseconds');

              return updateState(store, EAuthActions.REFRESH_TOKEN_RESPONSE, ({ loadingProps, errorProps }) => ({
                tokenData: response,
                refreshTokenExpires: newExpireDate.toISOString(),
                logoutIdToken: null,
                ...withoutLoading(ELoadingEntity.AUTH_TOKEN_DATA, loadingProps),
                ...withoutError(ELoadingEntity.AUTH_TOKEN_DATA, errorProps),
              }))
            }),
            catchError((error) =>
              of(
                updateState(store, EAuthActions.REFRESH_TOKEN_FAILURE, ({ loadingProps, errorProps }) => ({
                  ...withoutLoading(ELoadingEntity.AUTH_TOKEN_DATA, loadingProps),
                  ...withError(ELoadingEntity.AUTH_TOKEN_DATA, { error, errorParams: { type, refreshToken } }, errorProps),
                }))
              )
            )
          )
        })
      )
    );

    const clearError = (loadingEntity: ELoadingEntity) => {
      updateState(store, EAuthActions.CLEAR_ERROR, ({ errorProps }) => ({
        ...withoutError(loadingEntity, errorProps),
      }));
    }

    return {
      getAuthTokenData,
      refreshToken,
      logoutAndGetId,
      resetState,
      setRefreshTokenExpires,
      clearRefreshTokenExpires,
      clearError,
    };
  }),

) {}
